import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { AppointmentPurpose } from 'src/app/samwebDescription/samwin/AppointmentPurpose';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { AppointmentEntryRemoteDto } from 'src/generated/signalR/appointment-entry-remote-dto';
import { DirectoryEntryRemoteDto } from 'src/generated/swagger';
import { AppointmentsState } from '../../dashboard/appointments/appointments.state';
import { DirectoryBrowserBehaviourState } from '../../dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-browser-behaviour/directory-browser-behaviour.state';
import { DirectoryDataBehaviourState } from '../../dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-data/directory-data-behaviour.state';
import { DirectorySettingsState } from '../../dashboard/brick-wall/brick-settings/specific-brick-settings/directory-settings/directory-settings.state';
import { EnabledFeaturesState } from '../../session/enabled-features/enabled-features.state';
import { CloseAppointmentDialog, OpenAppointmentDialog } from './appointment-dialog.actions';

export interface AppointmentDialogStateModel {
   purpose: AppointmentPurpose;
   isOpen: boolean;
   orgId: number;
   brickId: string;
}
@State<AppointmentDialogStateModel>({
   name: 'appointmentDialogState',
   defaults: {
      purpose: AppointmentPurpose.SCHEDULER,
      isOpen: false,
      orgId: null,
      brickId: null,
   },
})
@Injectable()
export class AppointmentDialogState {
   constructor(private store: Store) {}

   @Selector([AppointmentDialogState])
   public static getPurpose(state: AppointmentDialogStateModel): AppointmentPurpose {
      return state.purpose;
   }

   @Selector([AppointmentDialogState])
   public static isAppointmentSchedulerOpen(state: AppointmentDialogStateModel): boolean {
      return state.isOpen;
   }

   @Selector([AppointmentDialogState, AppointmentsState.getAppointmentsForDirectoryEntry])
   public static getAppointmentsOfSelectedDirectoryEntry(
      state: AppointmentDialogStateModel,
      getAppointmentsForDirectoryEntryFn: (orgId: number) => AppointmentEntryRemoteDto[]
   ): AppointmentEntryRemoteDto[] {
      return getAppointmentsForDirectoryEntryFn(state.orgId);
   }

   @Selector([AppointmentDialogState, DirectoryDataBehaviourState.getDirectoryEntryById])
   public static currentDirectoryEntry(
      state: AppointmentDialogStateModel,
      getSelectedEntryByOrgId: (brickId: string, orgId: number) => DirectoryEntryRemoteDto
   ): DirectoryEntryRemoteDto {
      return getSelectedEntryByOrgId(state.brickId, state.orgId);
   }

   @Selector([AppointmentDialogState, DirectorySettingsState.getSchedulerSelectedView])
   public static getCurrentView(state: AppointmentDialogStateModel, getSchedulerSelectedView): number {
      return getSchedulerSelectedView(state.brickId);
   }

   @Selector([AppointmentDialogState, DirectorySettingsState.getSchedulerShowBusinessHours])
   public static getShowBusinessHours(state: AppointmentDialogStateModel, getSchedulerShowBusinessHours): number {
      return getSchedulerShowBusinessHours(state.brickId);
   }

   @Action(OpenAppointmentDialog)
   public openAppointmentDialog(ctx: StateContext<AppointmentDialogStateModel>, action: OpenAppointmentDialog) {
      // at least "Attendant" is required
      if (!this.store.selectSnapshot(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_ATTENDANT))) {
         console.debug('Agent is not allowed to open the appointment dialog');
         return;
      }
      if (_.isNil(action.orgId)) {
         action.orgId = this.store.selectSnapshot(DirectoryBrowserBehaviourState.getSelectedDirectoryID)(action.brickId);
      }
      ctx.patchState({
         isOpen: true,
         orgId: action.orgId,
         brickId: action.brickId,
      });
   }

   @Action(CloseAppointmentDialog)
   public closeAppointmentDialog(ctx: StateContext<AppointmentDialogStateModel>, action: CloseAppointmentDialog) {
      ctx.patchState({
         isOpen: false,
      });
   }
}
